/**
 * Shared client-side helpers for the Finances module's Cloud Function calls.
 * Centralizes the Cloud Functions base URL so the Balance Sheet card and the
 * Cashflow history refresh button stay in sync.
 */

import { crmAuthedFetch } from './crmAuthedFetch';

export const FUNCTIONS_URL = 'https://us-central1-lctf-projects.cloudfunctions.net';

/** POST helper that throws on a non-OK or non-`success: true` payload. */
async function postFn<T extends Record<string, unknown>>(
  path: string,
  body: Record<string, unknown> = {},
): Promise<T> {
  const res = await crmAuthedFetch(`${FUNCTIONS_URL}/${path}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  let data: Record<string, unknown> | null = null;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Cloud function ${path} returned invalid JSON (HTTP ${res.status})`);
  }
  if (!res.ok || !data?.success) {
    throw new Error(String(data?.error || `Cloud function ${path} returned HTTP ${res.status}`));
  }
  return data as T;
}

export interface CashflowRefreshSummary {
  monthCount: number;
  accountCount: number;
  rangeStart: string;
  rangeEnd: string;
}

/** Refresh the cached QuickBooks Balance Sheet snapshot. */
export async function refreshBalanceSheet(): Promise<void> {
  await postFn('getQuickBooksBalanceSheet');
}

/**
 * Refresh the cached QuickBooks Profit & Loss history (Cash + Accrual basis).
 * Data lands in `financeSettings/cashflowHistoryCache`; this returns a summary only.
 * @param monthsBack 1-36 months back from the last full month. Defaults to 36.
 */
export async function refreshCashflowHistory(monthsBack = 36): Promise<CashflowRefreshSummary> {
  const data = await postFn<Record<string, unknown>>(
    'getQuickBooksCashflowHistory',
    { monthsBack },
  );
  const cash = data.cash as { months?: string[]; accounts?: unknown[] } | undefined;
  const monthCount = Number(data.monthCount) || cash?.months?.length || 0;
  const accountCount = Number(data.accountCount) || cash?.accounts?.length || 0;
  return {
    monthCount,
    accountCount,
    rangeStart: String(data.rangeStart || ''),
    rangeEnd: String(data.rangeEnd || ''),
  };
}
